import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { ChevronRight, Sparkles, MapPin, X, Loader2 } from "lucide-react";
import { heroSlides, valueProps, categoryHeroImage, categoryHeroCopy } from "../mock";
import { useEnabledCategories } from "../hooks/useEnabledCategories";
import { catLabel } from "../utils/categoryLabel";
import { useContent } from "../hooks/useContent";
import api from "../api";
import DealCard from "../components/dashboard/DealCard";
import { TouristButton } from "../components/dashboard/TouristMode";
import { OnboardingChecklist } from "../components/dashboard/OnboardingChecklist";
import { LoyaltyOffer } from "../components/dashboard/LoyaltyOffer";
import { useLocationFilter } from "../context/LocationContext";
import { useFx } from "../context/FxContext";
import { useLang } from "../context/LanguageContext";
import { isLive } from "../utils/countdown";
import { distanceKm, formatDistance } from "../utils/geo";
import { withBoostOrder, withFeaturedOrder } from "../utils/boost";
import { CURRENCIES } from "../utils/currency";
import { MAP_HOME_ON_MOBILE, CLASSIC_HOME_KEY } from "../config/home";
import { thumb } from "../utils/image";
import { NearestCityBanner } from "../components/NearestCityBanner";

const HeroCarousel = ({ dealCount, categoryCount, maxDiscount = 0, topCategory = null }) => {
  const [idx, setIdx] = useState(0);
  const { t } = useLang();
  const cmsRaw = useContent("home");
  const cms = cmsRaw || {};
  const cmsReady = cmsRaw !== null;
  // Admin slides win; otherwise the built-in, translated ones — themed with a curated image that
  // matches the current top live deal's category (falls back to the slide's own image if no deals).
  const cmsSlides = (cms.slides || []).filter((s) => s.title || s.image);
  const heroImg = topCategory ? categoryHeroImage(topCategory) : null;
  const catCopy = topCategory ? categoryHeroCopy(topCategory) : null;
  const list = cmsSlides.length
    ? cmsSlides.map((s, i) => ({ id: `cms-${i}`, title: s.title, subtitle: s.subtitle, badge: s.badge,
                                 cta: s.ctaLabel, image: s.image }))
    : catCopy
      ? [{ id: `cat-${topCategory}`, title: `${t(catCopy.titleKeys[0])}\n${t(catCopy.titleKeys[1])}`,
           subtitle: t(catCopy.subtitleKey), badge: null, image: heroImg }]
      : heroSlides.map((s) => ({ id: s.id, title: `${t(s.titleKeys[0])}\n${t(s.titleKeys[1])}`,
                                 subtitle: t(s.subtitleKey), badge: t(s.badgeKey), image: heroImg || s.image }));
  const transition = cms.transition || "fade";
  const every = Math.max(2000, Number(cms.intervalMs) || 5000);
  useEffect(() => {
    if (list.length < 2) return undefined;
    const tm = setInterval(() => setIdx((i) => (i + 1) % list.length), every);
    return () => clearInterval(tm);
  }, [list.length, every]);
  const slide = list[idx % list.length] || list[0];
  const anim = transition === "none" ? "" : transition === "slide" ? "lp-slide-in" : "lp-fade";
  // The discount badge is derived from real live deals (already country-filtered by the API), so it
  // can never claim a saving that isn't actually on offer. No qualifying deal -> no badge.
  const badge = maxDiscount > 0 ? t("home.upToOff", { n: maxDiscount }) : null;

  if (!cmsReady) {
    return <div className="rounded-3xl bg-[#0f2a1a]/90 min-h-[320px] animate-pulse" data-testid="home-hero-skeleton" />;
  }

  return (
    <div className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white min-h-[320px] flex" data-testid="home-hero">
      <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[#16a34a]/40 blur-3xl" />
      <div className="absolute left-1/3 bottom-0 h-48 w-48 rounded-full bg-emerald-400/20 blur-2xl" />
      <div className="relative z-10 flex-1 p-6 sm:p-9 md:p-11 flex flex-col justify-center w-full md:max-w-[56%]">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200 backdrop-blur w-fit mb-4"><Sparkles size={13} /> {t("home.todaysBest")}</span>
        <h2 key={slide.id} className={`${anim} text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.05] mb-4 whitespace-pre-line`} data-testid="hero-title">{slide.title}</h2>
        <p className="text-sm md:text-base text-emerald-100/80 mb-6 max-w-md">{slide.subtitle}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/browse" data-testid="hero-explore-btn" className="group inline-flex items-center gap-2 bg-white text-[#0f2a1a] font-bold px-6 py-3 rounded-xl w-fit hover:bg-white/90 transition-colors">{slide.cta || t("home.explore")} <ChevronRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></Link>
          <Link to="/categories" className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-5 py-3 rounded-xl hover:bg-white/10 transition-colors">{t("home.browseCategories")}</Link>
        </div>
        <div className="flex items-center gap-2 mt-6 text-sm">
          <span className="rounded-full bg-white/95 px-4 py-1.5 font-bold text-[#0f2a1a]" data-testid="hero-deal-count">{t("home.liveDeals", { n: dealCount })}</span>
          <span className="rounded-full border border-white/25 px-4 py-1.5 font-semibold text-white/90">{t("home.categoriesCount", { n: categoryCount })}</span>
        </div>
        <div className="flex gap-2 mt-5">
          {list.map((s, i) => (<button key={s.id} onClick={() => setIdx(i)} className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/40 hover:bg-white/60"}`} aria-label={t("home.slideN", { n: i + 1 })} />))}
        </div>
      </div>
      <div className="relative z-10 hidden md:flex items-center justify-center flex-1">
        {slide.image && <img key={slide.image} src={thumb(slide.image, 1200)} alt="" decoding="async" className={`${anim} h-full w-full object-cover`} />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2a1a] via-[#0f2a1a]/20 to-transparent" />
        {badge && <span data-testid="hero-discount-badge" className="absolute top-8 right-8 bg-[#f5b942] text-[#0f2a1a] font-extrabold text-lg px-4 py-3 rounded-2xl -rotate-6 shadow-lg text-center leading-tight">{badge}</span>}
      </div>
    </div>
  );
};

const SectionHead = ({ title, to, viewAll }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-xl font-extrabold text-foreground">{title}</h2>
    <Link to={to} className="group inline-flex items-center gap-1 text-sm font-semibold text-[#16a34a] hover:underline">{viewAll} <ChevronRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" /></Link>
  </div>
);

// Admin-written copy for a homepage block. Nothing renders unless they've written something.
const Blurb = ({ html, id }) => (html ? (
  <div className="lp-rich text-sm text-muted-foreground mb-4" data-testid={`home-copy-${id}`}
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
) : null);

const Dashboard = () => {
  const categories = useEnabledCategories();
  const [deals, setDeals] = useState([]);
  const { tourist, country, city, gps, place, unit, setGps, setCountry, setPlace, requestLocation } = useLocationFilter();
  const { t } = useLang();
  const { displayCurrency, setDisplayCurrency } = useFx();
  const [curDismissed, setCurDismissed] = useState(() => localStorage.getItem("lp-cur-prompt") === "off");
  const [promptDismissed, setPromptDismissed] = useState(() => localStorage.getItem("lp-loc-prompt") === "off");
  const [locating, setLocating] = useState(false);
  useEffect(() => {
    const params = {};
    if (country) params.country = country;
    if (city) params.city = city;
    api.get("/deals", { params }).then((r) => setDeals(r.data.filter(isLive))).catch(() => {});
  }, [country, city]);
  // Count exactly the tiles shown in the Shop-by-Category grid so the badge always adds up.
  const categoryCount = useMemo(() => categories.filter((c) => c.name !== "More").length, [categories]);

  // Attach distance to each deal (nearest branch) when the shopper has shared a location.
  const located = useMemo(() => {
    if (!gps) return deals;
    return deals
      .map((d) => {
        const bs = (d.branches && d.branches.length ? d.branches : [{ lat: d.lat, lng: d.lng }]).filter((b) => b.lat != null);
        const km = bs.length ? Math.min(...bs.map((b) => distanceKm(gps.lat, gps.lng, b.lat, b.lng))) : null;
        return { ...d, _distanceKm: km, _distanceLabel: km != null ? formatDistance(km, unit) : "" };
      })
      .sort((a, b) => (a._distanceKm ?? 1e9) - (b._distanceKm ?? 1e9));
  }, [deals, gps, unit]);

  // Featured is now one global spot per category — no radius gate. Local and international shops
  // sit in the same row; we only order by nearness when the shopper has shared a location.
  const maxDiscount = useMemo(
    () => deals.reduce((m, d) => Math.max(m, Number(d.discount) || 0), 0),
    [deals],
  );
  const featured = useMemo(
    () => withFeaturedOrder(located.filter((d) => d.featured), (d) => d._distanceKm),
    [located],
  );

  const shareLocation = async () => {
    setLocating(true);
    const ok = await requestLocation();
    if (ok) {
      setCountry("");
      try {
        const g = JSON.parse(localStorage.getItem("lp-gps") || "null");
        if (g) {
          const { data } = await api.get("/reverse-geocode", { params: g });
          setPlace(data.city || (data.formatted ? data.formatted.split(",")[0] : "") || "Near me");
        }
      } catch { /* keep default place */ }
    }
    setLocating(false);
  };
  const dismissPrompt = () => { setPromptDismissed(true); localStorage.setItem("lp-loc-prompt", "off"); };
  const showLocPrompt = !gps && !country && !city && !tourist && !promptDismissed;

  const placeLabel = gps ? (place && place !== "your location" ? place : "you") : (city || country);

  const cmsHomeRaw = useContent("home");
  const cmsHome = cmsHomeRaw || {};
  const cmsReady = cmsHomeRaw !== null;
  const copy = cmsHome.copy || {};
  const homeBlocks = {
    categories: (
      <section>
        <SectionHead title={t("home.shopByCategory")} to="/categories" viewAll={t("home.viewAll")} />
        <Blurb html={copy.categories} id="categories" />
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
          {categories.map((c) => {
            const Icon = c.icon;
            const target = c.name === "More" ? { to: "/categories" }
              : c.name === "Near Expiry" ? { to: "/near-expiry" }
              : { to: "/browse", state: { category: c.name } };
            return (
              <Link key={c.slug} to={target.to} state={target.state} data-testid={`home-category-${c.name}`} className="group flex flex-col items-center gap-2 rounded-2xl py-2 transition-all duration-300 hover:-translate-y-1" style={{ "--cat": c.color }}>
                <span className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ background: c.bg }}>
                  <Icon size={24} style={{ color: c.color }} />
                </span>
                <span className="text-xs font-semibold text-foreground text-center leading-tight transition-colors group-hover:text-[color:var(--cat)]">{catLabel(c.name, t)}</span>
              </Link>
            );
          })}
        </div>
      </section>
    ),
  };
  const nearTitle = placeLabel
    ? (placeLabel === "you" ? t("home.topDealsNearYou") : t("home.topDealsNear", { place: placeLabel }))
    : t("home.topDeals");
  const featuredTitle = gps || city ? t("home.featuredNearYou") : (country ? t("home.featuredIn", { place: country }) : t("home.featuredDeals"));

  // Top Deals row excludes any deal already shown in the Featured row (no duplicates on the page).
  const topDeals = useMemo(() => {
    const shown = new Set(featured.slice(0, 6).map((d) => d.id));
    const pool = located.filter((d) => !shown.has(d.id));
    return withBoostOrder(pool, (d) => d._distanceKm).slice(0, 4);
  }, [located, featured]);

  // Category of the single top live deal, used to theme the hero with a curated image.
  const topCategory = useMemo(
    () => (featured[0] || topDeals[0] || located[0])?.category || null,
    [featured, topDeals, located],
  );

  homeBlocks.featured = featured.length > 0 ? (
    <section data-testid="featured-section">
      <SectionHead title={featuredTitle} to="/browse" viewAll={t("home.viewAll")} />
      <Blurb html={copy.featured} id="featured" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
        {featured.slice(0, 6).map((d) => (<DealCard key={d.id} deal={d} />))}
      </div>
    </section>
  ) : null;
  homeBlocks.nearby = (
    <section>
      <SectionHead title={nearTitle} to="/browse" viewAll={t("home.viewAll")} />
      <Blurb html={copy.nearby} id="nearby" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
        {topDeals.map((d) => (<DealCard key={d.id} deal={d} />))}
      </div>
    </section>
  );
  homeBlocks.values = (
    <section>
      <Blurb html={copy.values} id="values" />
      <div className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-3 gap-6">
        {valueProps.map((v) => {
          const Icon = v.icon;
          return (
            <div key={v.titleKey} className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: v.bg }}><Icon size={22} style={{ color: v.color }} /></span>
              <div><h3 className="font-bold" style={{ color: v.color }}>{t(v.titleKey)}</h3><p className="text-sm text-muted-foreground">{t(v.textKey)}</p></div>
            </div>
          );
        })}
      </div>
    </section>
  );
  const DEFAULT_ORDER = ["categories", "featured", "nearby", "values"];
  // Until the admin's section switches land, render nothing rather than a section they turned off.
  const homeOrder = (!cmsReady ? []
    : cmsHome.sections?.length
      ? cmsHome.sections.filter((s) => s.on !== false).map((s) => s.id)
      : DEFAULT_ORDER).filter((id) => homeBlocks[id]);

  return (
    <div className="space-y-8 lp-fade-up">
      <NearestCityBanner />
      {(MAP_HOME_ON_MOBILE || cmsHome.mapHomeOnMobile) && (
        <div className="md:hidden flex items-center justify-between gap-2 rounded-2xl border border-border bg-card px-3 py-2" data-testid="map-home-switch">
          <span className="text-xs text-muted-foreground">{t("map.homeClassicNote")}</span>
          <button onClick={() => { localStorage.removeItem(CLASSIC_HOME_KEY); window.location.reload(); }} data-testid="map-home-open"
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#16a34a] hover:bg-[#15803d] px-3 py-2 rounded-xl transition-colors">
            <MapPin size={13} /> {t("map.openMap")}
          </button>
        </div>
      )}
      <HeroCarousel dealCount={deals.length} categoryCount={categoryCount} maxDiscount={maxDiscount} topCategory={topCategory} />

      <OnboardingChecklist />

      <LoyaltyOffer />

      {showLocPrompt && (
        <div data-testid="location-prompt" className="relative overflow-hidden rounded-2xl border border-[#16a34a]/25 bg-[#e7f6ec] dark:bg-[#16341f] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="h-12 w-12 rounded-xl bg-[#16a34a] text-white flex items-center justify-center shrink-0"><MapPin size={22} /></span>
          <div className="flex-1">
            <h3 className="font-extrabold text-foreground">{t("home.locPromptTitle")}</h3>
            <p className="text-sm text-muted-foreground mt-0.5">{t("home.locPromptDesc")}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={shareLocation} disabled={locating} data-testid="location-prompt-share" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-70 text-white font-bold px-5 py-2.5 rounded-xl transition-colors">
              {locating ? <><Loader2 size={16} className="animate-spin" /> {t("home.locating")}</> : <><MapPin size={16} /> {t("home.shareLocation")}</>}
            </button>
            <button onClick={dismissPrompt} data-testid="location-prompt-dismiss" className="h-10 w-10 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Dismiss"><X size={18} /></button>
          </div>
        </div>
      )}

      {!displayCurrency && !curDismissed && (
        <div data-testid="currency-prompt" className="relative overflow-hidden rounded-2xl border border-[#16a34a]/25 bg-[#e7f6ec] dark:bg-[#16341f] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="h-12 w-12 rounded-xl bg-[#16a34a] text-white flex items-center justify-center shrink-0 font-extrabold text-lg">$</span>
          <div className="flex-1">
            <h3 className="font-extrabold text-foreground">{t("dsh.seePricesInYour")}</h3>
            <p className="text-sm text-muted-foreground mt-0.5">{t("dsh.pickYourCurrencyAnd")}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              data-testid="currency-prompt-select"
              defaultValue=""
              onChange={(e) => { if (e.target.value) { setDisplayCurrency(e.target.value); } }}
              className="h-11 rounded-xl border border-[#16a34a]/40 bg-card px-3 text-sm font-semibold text-foreground outline-none focus:border-[#16a34a]"
            >
              <option value="" disabled>{t("dsh.chooseCurrency")}</option>
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <button onClick={() => { setCurDismissed(true); localStorage.setItem("lp-cur-prompt", "off"); }} data-testid="currency-prompt-dismiss" className="h-11 w-11 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Dismiss"><X size={18} /></button>
          </div>
        </div>
      )}

      {!tourist && <TouristButton variant="home" />}

      {cmsHome.introHtml && (
        <div className="lp-rich bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground"
          data-testid="home-intro-copy"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cmsHome.introHtml) }} />
      )}

      {!cmsReady && (
        <div className="space-y-8" data-testid="home-sections-skeleton">
          <div className="h-28 rounded-2xl border border-border bg-secondary/60 animate-pulse" />
          <div className="h-64 rounded-2xl border border-border bg-secondary/60 animate-pulse" />
        </div>
      )}
      {homeOrder.map((id) => <React.Fragment key={id}>{homeBlocks[id]}</React.Fragment>)}

      {cmsHome.outroHtml && (
        <div className="lp-rich bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground"
          data-testid="home-outro-copy"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cmsHome.outroHtml) }} />
      )}
    </div>
  );
};

export default Dashboard;
